import * as React from 'react';
import _ from 'lodash';

import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';

export function canBeBooked(experience: consumer.IOffering): boolean {
  return !getIsSoldOut(experience) && !getIsLocked(experience) && !getHasNoInventory(experience);
}

export function getIsSoldOut(experience: consumer.IOffering): boolean {
  return experience.state === 'SOLD';
}

export function getIsLocked(experience: consumer.IOffering): boolean {
  return experience.state === 'LOCKED';
}

export function getHasNoInventory(experience: consumer.IOffering): boolean {
  return experience.state === 'NO_TICKET_GROUPS';
}
export function getEventBookUrl(experience: core.TicketType | consumer.Offering): string {
  return `/event/${experience.id}/book`;
}

export function getTypeContent(
  type: consumer.Offering.Type,
  sizeRange: number[],
  descriptiveVariety?: core.DescriptiveVariety,
  singleUnitQuantity?: number
) {
  const typeContent: React.ReactNode = type && getTypeString(type, descriptiveVariety);

  return typeContent;
}

/* Generates the "for parties of x to y" text for non-TTG experience types, null for others */
export function getSizeContent(type: consumer.Offering.Type, sizeRange: number[]) {
  switch (type) {
    case 'ORDER_DELIVERY':
    case 'ORDER_PICKUP': {
      return null;
    }
    default:
      return <span> for&nbsp;{sizeRange && getSizeRangeString(sizeRange)}</span>;
  }
}

/* Generates the "(serves x)" text for TTG experience types that support it, null for all others */
export function getServesContent(
  descriptiveVariety?: core.DescriptiveVariety,
  singleUnitQuantity?: number
) {
  if (descriptiveVariety === 'TOGO_PACKAGE' && singleUnitQuantity && singleUnitQuantity > 1) {
    return <span> (serves {singleUnitQuantity})</span>;
  }
  return null;
}

export function getTypeString(
  type: consumer.Offering.Type,
  descriptiveVariety?: core.DescriptiveVariety
): string {
  // Some descriptive varieties have special overloads
  switch (descriptiveVariety) {
    case 'TOGO_PACKAGE':
      return 'Prepaid package';
  }

  switch (type) {
    case 'PRIX_FIXE':
      return 'Prepaid reservation';
    case 'DEPOSIT':
      return 'Reservation';
    case 'RESERVATION':
      return 'Reservation';
    case 'DEPRECATED_EVENT':
    case 'GA_EVENT':
    case 'SEATED_EVENT':
      return 'Event';
    case 'DEPRECATED_SPECIAL_TABLE':
      return ''; //TODO(jacob): Added as part of adding assertNever below to
    // avoid changing semantics. Probably should be something else.
    case 'ORDER_DELIVERY':
      return 'Prepaid delivery';
    case 'ORDER_PICKUP':
      return 'Prepaid pickup';
    case 'TABLESIDE':
      return 'Tableside';
    default:
      assertNever(type);
      return '';
  }
}

export function getSizeRangeString(sizeRange: number[]): string {
  const minSize = _.min(sizeRange);
  const maxSize = _.max(sizeRange);

  if (!minSize && !maxSize) {
    return 'your party';
  }

  if (minSize === maxSize) {
    return `parties of ${maxSize}`;
  }

  return `parties of ${minSize} to ${maxSize}`;
}
